import {
    DeleteOutlineRounded,
    FiberManualRecordRounded,
    MoreVertRounded,
} from '@mui/icons-material';
import {
    TimelineConnector,
    TimelineContent,
    TimelineDot,
    TimelineItem,
    TimelineSeparator,
} from '@mui/lab';
import {
    Avatar,
    Box,
    Button,
    Icon,
    IconButton,
    InputBase,
    ListItemIcon,
    Menu,
    MenuItem,
    Typography,
} from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAuthInfo } from '@propelauth/react';

const EditActivity = ({ defaultValue, onCancel, onUpdate }) => {
    const { t } = useTranslation();
    const [value, setValue] = useState(defaultValue);
    return (
        <>
            <InputBase
                fullWidth
                multiline
                maxRows={3}
                minRows={1}
                value={value}
                onChange={e => setValue(e.target.value)}
                sx={{
                    backgroundColor: 'surfaceContainerHighest.main',
                    borderRadius: 'inherit',
                    px: 1,
                    mb: 1,
                }}
            />
            <Box display="flex" justifyContent="flex-end" gap={1}>
                <Button size="small" variant="text" onClick={onCancel}>
                    {t('Cancel')}
                </Button>
                <Button
                    size="small"
                    disabled={!value}
                    onClick={() => onUpdate(value)}
                >
                    {t('Save')}
                </Button>
            </Box>
        </>
    );
};

const ActivityNote = ({
    activity,
    onEdit,
    onCancel,
    isEditing,
    onDelete,
    onUpdate,
}) => {
    const { t } = useTranslation();
    const authInfo = useAuthInfo();
    const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);

    const isOwner = authInfo.user.userId === activity.user_id;

    return (
        <TimelineItem>
            <TimelineSeparator>
                <TimelineDot sx={{ p: 0, border: 0 }}>
                    <Avatar
                        sx={{
                            width: 32,
                            height: 32,
                        }}
                        src={activity.user_picture_url}
                    />
                </TimelineDot>
                <TimelineConnector />
            </TimelineSeparator>
            <TimelineContent sx={{ width: '100%' }}>
                <Box
                    sx={{
                        backgroundColor: 'surfaceContainer.main',
                        padding: 1.5,
                        borderRadius: 2,
                        borderTopLeftRadius: 0,
                    }}
                >
                    <Box
                        display="flex"
                        justifyContent="space-between"
                        alignItems="center"
                        marginBottom={0.5}
                    >
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography variant="titleSmall">
                                {activity.user_name}
                            </Typography>
                            <Typography variant="caption" display="inline-flex">
                                <FiberManualRecordRounded
                                    fontSize="inherit"
                                    color="inherit"
                                    sx={{
                                        mx: 0.5,
                                        alignSelf: 'center',
                                        width: 8,
                                        height: 8,
                                    }}
                                />
                                {activity.activity_date.toFriendlyDateTime()}
                            </Typography>
                        </Box>
                        <IconButton
                            size="small"
                            color="primary"
                            sx={{ p: 0 }}
                            onClick={event => setAnchorEl(event.currentTarget)}
                        >
                            <MoreVertRounded fontSize="inherit" />
                        </IconButton>
                    </Box>
                    {isEditing ? (
                        <EditActivity
                            defaultValue={activity.note}
                            onCancel={onCancel}
                            onUpdate={onUpdate}
                        />
                    ) : (
                        <Typography variant="bodyMedium" color="GrayText">
                            {activity.note}
                        </Typography>
                    )}
                </Box>
            </TimelineContent>
            <Menu
                anchorEl={anchorEl}
                open={Boolean(anchorEl)}
                onClose={() => setAnchorEl(null)}
                MenuListProps={{ dense: true }}
                anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
                transformOrigin={{ vertical: 'top', horizontal: 'right' }}
            >
                {isOwner && (
                    <MenuItem
                        onClick={() => {
                            onEdit(true);
                            setAnchorEl(null);
                        }}
                    >
                        <ListItemIcon>
                            <Icon fontSize="small">edit_square</Icon>
                        </ListItemIcon>
                        {t('Edit')}
                    </MenuItem>
                )}
                <MenuItem
                    sx={{ color: 'error.main' }}
                    onClick={() => onDelete(activity.id)}
                >
                    <ListItemIcon>
                        <DeleteOutlineRounded />
                    </ListItemIcon>
                    {t('Delete')}
                </MenuItem>
            </Menu>
        </TimelineItem>
    );
};

export default ActivityNote;
